import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router";
import { BreadCrumb } from "primereact/breadcrumb";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";

import { doUserEnrollment, doUserUnenrollment } from "sj-ui-toolkit/src/api";
import { useEnrollments, useUserAssignments } from "sj-ui-toolkit/src/api/hooks";
import { useSettingsContext } from "sj-ui-toolkit/src/atlas/contexts/Settings/SettingsContextProvider";
import { getPathByCollectionKey } from "sj-ui-toolkit/src/api/user";
import { isEmpty } from "sj-ui-toolkit/src/utils";
import { transformPathData } from "sj-ui-toolkit/src/atlas/utils";
import { INTERNAL_ROUTES } from "sj-ui-toolkit/src/constants/routes";
import { useSmartNavigation } from "sj-ui-toolkit/src/helpers/navigation";

import { useLearnNavigationStack } from "../../context/LearnNavigationContext";
import { PathDetails } from "./PathDetails";
import { LessonsContainer } from "./LessonsContainer";
import EnrollmentPromptModal from "./EnrollmentPromptModal";
import { useLevelContext } from "sj-ui-toolkit/src/atlas/contexts/Level/LevelContextProvider";

const Path = ({ pathType = "Path" }) => {
  const { pathId } = useParams();
  const [pathData, setPathData] = useState({
    pathDetails: {},
    remainingLessons: [],
    completedLessons: [],
  });
  const { navigateTo } = useSmartNavigation();
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [noResultsFound, setNoResultsFound] = useState(false);
  const [isEnrolled, setIsEnrolled] = useState("loading");
  const [isEnrolling, setIsEnrolling] = useState(false);
  const [pendingLessonRoute, setPendingLessonRoute] = useState(null);
  const [currentLevel, setCurrentLevel] = useState(false);
  const [showEnrollmentModal, setShowEnrollmentModal] = useState(false);
  const [enrollmentModalDismissed, setEnrollmentModalDismissed] = useState(false);
  // const [isLevelLocked, setIsLevelLocked] = useState(false);

  const toast = useRef(null);
  const { data: currentPaths = [] } = useEnrollments();
  const { levels, refetchLevels } = useLevelContext();
  const { data: assignments = [] } = useUserAssignments();

  // Navigation stack
  const { isFromCatalog } = useLearnNavigationStack();

  const { settings } = useSettingsContext();
  const { strictAssignment } = settings || {};

  const hasOutstandingStrictAssignment = useMemo(() => {
    if (!strictAssignment || !assignments?.length) return false;
    return assignments.some(assignment => !assignment.completed);
  }, [assignments, strictAssignment]);

  const isInactivePath = !pathData?.pathDetails?.activated;
  const isLevelLocked =
    !pathData?.pathDetails?.enrolledInLevel && !pathData?.pathDetails?.enrolledInPath;

  const disabledEnrollButton =
    hasOutstandingStrictAssignment ||
    isInactivePath ||
    isLevelLocked ||
    pathData?.pathDetails?.status === "passed";

  console.log("PathDetails", { pathData });

  const breadcrumbHome = {
    label: INTERNAL_ROUTES.Learn.name,
    command: () => navigateTo(INTERNAL_ROUTES.Learn.route),
  };

  // Get back destination from breadcrumbs
  const getBackDestination = () => {
    if (breadcrumbs.length > 1) {
      const prev = breadcrumbs[breadcrumbs.length - 2];
      return {
        label: `Back to ${prev.label}`,
        action: prev.command,
      };
    }
    return {
      label: `Back to ${breadcrumbHome.label}`,
      action: breadcrumbHome.command,
    };
  };

  // useEffect(() => {}, [levels, pathData]);

  useEffect(() => {
    if (!isEmpty(levels) && !isEmpty(pathData)) {
      const currentLevelId = pathData.pathDetails?.levelId;

      if (!currentLevelId) return;

      const matchedLevel = levels.find(level => level.role?.levelId === currentLevelId);

      if (matchedLevel && matchedLevel?.role?.id != pathData.pathDetails.id) {
        setCurrentLevel(matchedLevel);
      }
    }
  }, [levels, pathData]);

  // useEffect(() => {
  //   // If user is not enrolled but has completed lessons, show enrollment modal
  //   if (isEnrolled === false && !isFromCatalog() && pathData?.completedLessons?.length) {
  //     setShowEnrollmentModal(true);
  //   }
  // }, [isEnrolled]);

  useEffect(() => {
    if (isEnrolled === "loading" && pathData?.pathDetails?.id && currentPaths) {
      const enrolled = currentPaths.some(
        currentPath => currentPath.roleId == pathData.pathDetails.id
      ); // double equals intentional
      setIsEnrolled(enrolled);
    }
  }, [currentPaths, pathData]);

  useEffect(() => {
    const fetchPath = async () => {
      if (!pathId) return;
      try {
        const response = await getPathByCollectionKey(pathId);
        const formattedPath = transformPathData(response);

        setPathData(formattedPath);

        // Build breadcrumbs based on origin
        const crumbs = [];
        if (isFromCatalog()) {
          crumbs.push({
            label: "Catalog",
            command: () => navigateTo(INTERNAL_ROUTES.Learn.routes.catalog.route),
          });
        }
        crumbs.push({ label: formattedPath.pathDetails?.title });

        setBreadcrumbs(crumbs);
      } catch (error) {
        console.error("Error fetching path:", error);
        setNoResultsFound(true);
      }
    };

    fetchPath();
  }, [pathId]);

  const handleEnroll = async overrideOtherPaths => {
    setIsEnrolling(true);
    try {
      await doUserEnrollment(pathData.pathDetails.id, overrideOtherPaths);
      setPathData(prev => ({
        ...prev,
        pathDetails: { ...prev.pathDetails, enrolledInPath: true },
      }));
      setIsEnrolled(true);
      refetchLevels();
      toast.current?.show({
        severity: "success",
        summary: "Enrolled",
        detail: "You have successfully enrolled in this path.",
        life: 3000,
      });

      // If there's a pending lesson, navigate to it after enrollment
      if (pendingLessonRoute) {
        navigateTo(pendingLessonRoute);
        setPendingLessonRoute(null);
      }
    } catch (_error) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to enroll in path. Please try again.",
        life: 3000,
      });
    } finally {
      setIsEnrolling(false);
    }
  };

  const handleUnenroll = async () => {
    try {
      const currentPath = currentPaths.find(
        currentPath => currentPath.roleId == pathData.pathDetails.id
      );
      if (!currentPath) return;

      await doUserUnenrollment(currentPath.roleId);

      setPathData(prev => ({
        ...prev,
        pathDetails: { ...prev.pathDetails, enrolledInPath: false },
      }));
      setEnrollmentModalDismissed(true);
      setIsEnrolled(false);
      refetchLevels();
      toast.current?.show({
        severity: "success",
        summary: "Unenrolled",
        detail: "You have successfully unenrolled from this path.",
        life: 3000,
      });
    } catch (_error) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to unenroll from path. Please try again.",
        life: 3000,
      });
    }
  };

  // Handler for when a lesson is clicked
  const handleLessonClick = lessonRoute => {
    // If user is not enrolled, show the enrollment prompt
    if (!isEnrolled) {
      setPendingLessonRoute(lessonRoute);
      setShowEnrollmentModal(true);
      return;
    }

    // User is enrolled, navigate directly
    navigateTo(lessonRoute);
  };

  const handleModalCancel = () => {
    // Navigate to lesson without enrolling
    if (pendingLessonRoute) {
      navigateTo(pendingLessonRoute);
      setPendingLessonRoute(null);
    }
    setShowEnrollmentModal(false);
  };

  const handleModalEnroll = async () => {
    await handleEnroll();
    setShowEnrollmentModal(false);
  };

  if (noResultsFound) {
    const backDestination = getBackDestination();
    return (
      <div className="my-8 flex flex-col gap-4">
        <h2 className="atlas-h2">Path Not Found</h2>
        <p className="body-regular">We couldn't find the Path that you were looking for.</p>
        <div className="mt-2">
          <Button label={backDestination.label} onClick={backDestination.action} />
        </div>
      </div>
    );
  }

  return (
    <div data-test-id="learn-path-page">
      <Toast ref={toast} />
      <BreadCrumb model={breadcrumbs} home={breadcrumbHome} />

      {!isEmpty(pathData.pathDetails) && (
        <PathDetails
          {...pathData.pathDetails}
          enrollInPath={handleEnroll}
          unEnrollInPath={handleUnenroll}
          isEnrolled={isEnrolled}
          pathType={pathType}
          currentLevel={currentLevel}
          hasOutstandingStrictAssignment={hasOutstandingStrictAssignment}
          isInactivePath={isInactivePath}
          isLevelLocked={isLevelLocked}
          disabledEnrollButton={disabledEnrollButton}
        />
      )}

      {pathData.remainingLessons.length > 0 && (
        <LessonsContainer
          lessons={pathData.remainingLessons}
          title="Lessons to do"
          inPath={pathType === "Path"}
          onLessonClick={handleLessonClick}
        />
      )}

      {pathData.completedLessons.length > 0 && (
        <LessonsContainer
          lessons={pathData.completedLessons}
          title="Completed Lessons"
          onLessonClick={handleLessonClick}
        />
      )}

      <EnrollmentPromptModal
        visible={showEnrollmentModal && !enrollmentModalDismissed && !disabledEnrollButton}
        onHide={handleModalCancel}
        onEnroll={handleModalEnroll}
        pathName={pathData.pathDetails?.title}
        isLoading={isEnrolling}
        currentLevel={currentLevel}
        title={pathData.pathDetails?.title}
      />
    </div>
  );
};

export default Path;
